import { SyncService } from "frontend/services/syncService";
import { useSortedFrames } from "frontend/subscriptions";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Layer, Rect, Stage } from "react-konva";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import useStateValue from "frontend/state/value";
import { useBoardValue } from "frontend/state/board-state";
import { noop } from "frontend/utils/fn-utils";
import { BoardContext, getElementProvider } from "elements/index";
import ElementComponent from "elements/base/konva-component";
import { IntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import { MondayItemManagerProvider } from "frontend/canvas-designer-new/integrations/components/monday-item-manager-context";
import { CanvasProvider } from "frontend/canvas-designer-new/canvas-context";
import { CanvasElement } from "frontend/canvas-designer-new/canvas-element";
import { getElementTypeForId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import { useDebounce, useElementSize } from "usehooks-ts";

const FrameThumbnailUnmemoized = ({
  syncService,
  frame,
  width,
  height,
  className,
}: {
  syncService: SyncService<RW> | null;
  frame: any;
  width: string;
  height: string;
  className?: string;
}) => {
  const stageRef = useRef<any>(null);
  const [_stageContainerRef, _stageSize] = useElementSize();
  const [stageOffset, setStageOffset] = useState({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState(1);
  const [stageBackground, setStageBackground] = useState("#FFFFFF");
  const [{ board, pusherChannel }] = useBoardValue();
  const { elements, framesMap } = useSortedFrames(syncService?.getReplicache());
  const stageSize = useDebounce(_stageSize, 200);
  const [appState, _] = useStateValue();
  const elementIds = useMemo(() => framesMap[`frame-${frame.id}`] || [], [framesMap, frame.id]);

  useEffect(() => {
    if (!frame) {
      return;
    }
    const position = { x: frame.value.x, y: frame.value.y };

    const fWidth = frame.value.width * (frame.value.scaleX ?? 1);
    const fHeight = frame.value.height * (frame.value.scaleY ?? 1);
    const scale = Math.min(stageSize.width / fWidth, stageSize.height / fHeight);

    // fix centered position with rendered width/height
    const rWidth = fWidth * scale;
    const rHeight = fHeight * scale;
    if (rWidth < stageSize.width) {
      const diff = stageSize.width - rWidth;
      position.x -= diff / 2 / scale;
    }
    if (rHeight < stageSize.height) {
      const diff = stageSize.height - rHeight;
      position.y -= diff / 2 / scale;
    }

    setStageOffset(position);
    setStageScale(scale);
    setStageBackground(frame.value.fill ?? "#FFFFFF");
  }, [stageSize, elements, frame]);

  const renderElements = useCallback(
    (ids: string[]) => {
      const isLoggedIn = !(!appState.user || appState.user.isGuest);
      const context: BoardContext = {
        documentId: board!.documentId,
        boardId: board!.id,
        reflect: syncService?.getReplicache() ?? null,
        isReadOnly: true,
        isLoggedIn,
        selectedElementIds: [],
        setSelectedIds: () => {},
        setStageState: () => {},
        hiddenElementIds: [],
        editingElementId: null,
        editingElementLinkId: null,
        undoRedoStack: undefined,
      };
      return ids.map((id) => {
        const type = getElementTypeForId(id);
        const provider = getElementProvider(type);
        const element = elements.find((el) => id.includes(el.id))?.value;

        if (provider) {
          if (provider.isFFEnabled()) {
            // if provider exists it means it support the new infra
            return (
              <ElementComponent
                key={id}
                id={id}
                elementData={element}
                type={type}
                context={context}
                onResize={() => {}}
              />
            );
          }
          return null;
        }
        return (
          <CanvasElement
            documentId={board?.documentId}
            key={id}
            syncService={syncService}
            uniqueId={id}
            elementData={element}
            onResize={() => {}}
            onChangeElement={() => {}}
            isSelected={false}
            drawOutlineAroundElements={false}
            isEditing={false}
            isEditingLink={false}
            isFrameHighlighted={false}
            onElementsMutationEnded={() => {}}
            isSelectable={false}
            isReadOnly={true}
            patchCanvasElement={noop}
            patchAnything={noop}
          />
        );
      });
    },
    [elements, syncService, board]
  );

  return (
    <div ref={_stageContainerRef} className={className} style={{ width: width, height: height }}>
      <Stage
        ref={stageRef}
        offsetX={stageOffset.x}
        offsetY={stageOffset.y}
        width={Math.max(stageSize.width - 2, 1)}
        height={Math.max(stageSize.height - 2, 1)}
        scaleX={stageScale}
        scaleY={stageScale}
      >
        <CanvasProvider appState={appState} documentId={board?.documentId}>
          <IntegrationsProvider documentId={board!.documentId} pusherChannel={pusherChannel}>
            <MondayItemManagerProvider documentId={board!.documentId}>
              <Layer visible={true}>
                {/* Background rect for the frame */}
                <Rect
                  x={stageOffset.x}
                  y={stageOffset.y}
                  width={Math.max(stageSize.width, 1)}
                  height={Math.max(stageSize.height - 2, 1)}
                  scaleX={1 / stageScale}
                  scaleY={1 / stageScale}
                  fill={stageBackground}
                  cornerRadius={10}
                />
                {/* Render all elements for this frame */}
                {renderElements(elementIds)}
              </Layer>
            </MondayItemManagerProvider>
          </IntegrationsProvider>
        </CanvasProvider>
      </Stage>
    </div>
  );
};

export const FrameThumbnail = React.memo(FrameThumbnailUnmemoized);
